'use strict';

import debounce from 'lodash/debounce';
import * as progress from './progress';
import {initModules} from '../helpers/utilities';
import {appendParamsToURL, appendParamToURL} from '../helpers/urlUtilities';
import {setOverlay, unsetOverlay, allowScrollableAreas, addEventListenerList} from '../helpers/utilities';
import {disableBodyScroll, enableBodyScroll} from 'body-scroll-lock';
import {callbackOnElements} from '@borngroup/born-utilities';
import modules from '../helpers/modules';
import {plpColorStrap, resetProductAnchors} from '../search/index';

const
    SUGGESTIONS_URL_EL = document.querySelector('[data-suggestions-url]'),
    ENDPOINT = SUGGESTIONS_URL_EL ? SUGGESTIONS_URL_EL.getAttribute('data-suggestions-url') : '',
    MIN_CHARS = 3;

/**
 * Retrieves the closest search container, or the currently available one.
 */
function getSearchContainer(scope) {
    let selector = '[data-search-component="container"]';

    return scope ? scope.closest(selector) : document.querySelector(selector);
}

/**
 * Retrieves Suggestions element relative to scope
 *
 * @param {Object} scope - Search input field DOM element
 * @return {JQuery} - [data-search-component="suggestions-container"] element
 */
function getSuggestionsContainer(scope, emptyContainer) {
    let searchContainer = getSearchContainer(scope),
        suggestionsContainer = searchContainer ? searchContainer.querySelector('[data-search-component="suggestions-container"]') : false;

    if (emptyContainer && suggestionsContainer) {
        suggestionsContainer.textContent = '';

        searchContainer.classList.remove('set--has-results');
    }

    return suggestionsContainer;
}

function _handleSearchTracking(options) {
    if (options.formUrl) {
        let delayedPageView = options.delayPageView ? 1500 : 0;

        //Delay tracking instant search results to prevent logging "false" page views.
        debounce(function() {
            modules.tracking.init(null, {
                trackEvent: 'virtualPageview',
                trackObject: {
                    asyncURL: window.SFRA.Urls.GTMVirtualView,
                    reqPath: appendParamsToURL(window.SFRA.Urls.searchShow, {kwCAR: options.searchTerm, category: options.searchCategory}),
                    qString: options.searchTerm,
                    asyncParams: {
                        pageTemplate: 'Instantsearchpages'
                    }
                }
            });
        }, delayedPageView)();
        resetProductAnchors();
    }

    modules.tracking.init(null, {
        trackEvent: 'search',
        trackObject: {
            searchTerm: options.searchTerm,
            searchCategory: options.searchCategory || 'search'
        }
    });
}

/**
 * Process Ajax response for SearchServices-GetSuggestions
 *
 * @param {Object|string} response - Empty object literal if null response or string with rendered
 *                                   suggestions template contents
 */
function processResponse(response, formUrl, searchTerm) {
    let suggestionsContainer = getSuggestionsContainer(this),
        searchContainer = getSearchContainer(this);

    if (typeof response !== 'object') {
        suggestionsContainer.innerHTML = response;
        initModules(suggestionsContainer);

        searchContainer.classList.add('set--has-results');
        suggestionsContainer.classList.remove('set--invisible');

        _handleSearchTracking({formUrl, searchTerm, searchCategory: 'instantSearch', delayPageView: true});
    } else {
        suggestionsContainer.classList.add('set--invisible');
    }

    progress.hideLoader();
}

/**
 * Retrieve suggestions
 *
 * @param {Object} scope - Search field DOM element
 */
function getSuggestions() {
    let searchTerm = this.form['q'].value;

    if (searchTerm.length >= MIN_CHARS) {
        let formUrl = appendParamToURL(ENDPOINT, 'q', searchTerm);

        progress.showLoader(getSuggestionsContainer(this));

        if (this.form['cgid'] && this.form['cgid'].checked) {
            formUrl = appendParamToURL(formUrl, 'cgid', this.form['cgid'].value);
        }

        $.ajax({
            context: this,
            url: formUrl,
            method: 'GET',
            success: (response) => processResponse(response, formUrl, searchTerm),
        });
    } else {
        getSuggestionsContainer(this, true);
    }
}

/**
 * Prevents submitting an empty search from the searchbar.
 * @return {[type]} [description]
 */
function preventEmptySearch() {
    addEventListenerList('[data-search-component="form"]', 'submit', function(evt) {
        let currentInput = this.querySelector('[data-search-component="query-input"]');

        if (!currentInput.value.trim()) {
            evt.preventDefault();

            return false;
        } else {
            _handleSearchTracking({searchTerm: currentInput.value});
        }
    });
}

function setupSearchTrigger() {
    callbackOnElements('[data-search-component="trigger"]', function(currentTrigger) {
        if (currentTrigger && currentTrigger.toggle) {
            currentTrigger.toggle.afterSet = function() {
                let queryInputEl = currentTrigger.toggle.targetEl.querySelector('[data-search-component="query-input"]');

                disableBodyScroll(currentTrigger.toggle.targetEl, {allowTouchMove: allowScrollableAreas});

                setOverlay({trigger: 'searchbar'});

                getSuggestions.call(queryInputEl);
            };

            currentTrigger.toggle.afterUnset = function() {
                enableBodyScroll(currentTrigger.toggle.targetEl);

                unsetOverlay({trigger: 'searchbar'});
            };
        }
    });
    plpColorStrap();
}

/**
 * Toggle search bar off when megamenu is opened.
 */
export function closeSearch() {
    let searchTrigger = document.querySelector('[data-search-component="trigger"].toggle--active');

    if (searchTrigger && searchTrigger.toggle) {
        searchTrigger.toggle.unset();
    }
}

export default function() {
    let searchInputs = document.querySelectorAll('[data-search-suggestions] [data-search-component*="input"]'),
        /**
         * Use debounce to avoid making an Ajax call on every single key press by waiting a few
         * hundred milliseconds before making the request. Without debounce, the user sees the
         * browser blink with every key press.
         */
        debounceSuggestions = debounce(getSuggestions, 300);

    [].forEach.call(searchInputs, function(currentInput) {
        currentInput.addEventListener('input', debounceSuggestions);
    });

    preventEmptySearch();
    setupSearchTrigger();

    document.body.addEventListener('click', function(evt) {
        if (!evt.target.closest('[data-search-component="container"]')) {
            getSuggestionsContainer(false, true);
        }
    });
}
